/**
 * useIsSAContext - detects whether the current visitor is (or is
 * highly likely to be) a South Australian user. Multiple signals are
 * combined so we can safely gate partner surfaces that are SA-only
 * (e.g. House Inspect Australia) without hiding them from users who
 * clearly ARE in SA but haven't yet interacted with a MiniMatcher.
 *
 * Signals, in priority order (any positive signal returns true):
 *   1. Session contact `state === 'SA'` (highest confidence - user
 *      answered a Matcher question with SA)
 *   2. Anonymous brief `state === 'SA'` (they picked SA in a hub or
 *      journey selector)
 *   3. URL pathname contains an SA marker: `/adelaide`, `/-sa/` or
 *      state-specific SA hub slugs (e.g. `/find/sa/...`)
 *   4. Localstorage-cached previous SA answer (persists across
 *      sessionStorage clears)
 *
 * Returns { isSA: boolean, confidence: 'high' | 'medium' | 'low' | 'none' }
 * so callers can decide whether to render "SA only" tags for medium
 * confidence versus outright hiding for confirmed non-SA users.
 *
 * IMPORTANT: this hook returns { isSA: true } only when there is a
 * POSITIVE SA signal. Absence of any signal (unknown state) returns
 * { isSA: false, confidence: 'none' } so callers HIDE the partner
 * block from unknown-state users, matching the "hide from non-SA"
 * directive.
 */
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSessionContact } from './useSessionContact';
import useAnonymousBrief from './useAnonymousBrief';

const LS_KEY = 'bp.userState';
const SA_URL_MARKERS = ['/adelaide', '-adelaide', '/sa/', '/-sa', '/south-australia'];

export default function useIsSAContext() {
  const location = useLocation();
  const { contact } = useSessionContact();
  const { brief } = useAnonymousBrief();

  return useMemo(() => {
    // Signal 1: session contact
    if (contact?.state === 'SA') {
      try { window.localStorage?.setItem(LS_KEY, 'SA'); } catch (_) { /* ignore */ }
      return { isSA: true, confidence: 'high' };
    }
    // Any non-SA state means an explicit non-SA user
    if (contact?.state && contact.state !== 'SA') {
      return { isSA: false, confidence: 'high' };
    }
    // Signal 2: anonymous brief
    if (brief?.state === 'SA') {
      try { window.localStorage?.setItem(LS_KEY, 'SA'); } catch (_) { /* ignore */ }
      return { isSA: true, confidence: 'high' };
    }
    if (brief?.state && brief.state !== 'SA') {
      return { isSA: false, confidence: 'high' };
    }
    // Signal 3: URL markers
    const path = (location?.pathname || '').toLowerCase();
    if (SA_URL_MARKERS.some((m) => path.includes(m))) {
      return { isSA: true, confidence: 'medium' };
    }
    // Signal 4: cached localStorage
    try {
      const cached = window.localStorage?.getItem(LS_KEY);
      if (cached === 'SA') return { isSA: true, confidence: 'low' };
      if (cached && cached !== 'SA') return { isSA: false, confidence: 'low' };
    } catch (_) { /* ignore */ }
    // No signals - default to NOT SA to honour "hide from non-SA"
    return { isSA: false, confidence: 'none' };
  }, [contact?.state, brief?.state, location?.pathname]);
}
